import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// @mui
import { Box, Divider, Typography, MenuItem, Drawer } from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import useLocales from '../../hooks/useLocales';
import { dispatch, useSelector } from '../../redux/store';
import { fNumberDecimal } from '../../utils/formatNumber';
import { openConfirm } from '../../redux/slices/confirm';

// ----------------------------------------------------------------------

AccountInfo.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default function AccountInfo({ open, handleClose }) {
  const navigate = useNavigate();

  const { userProfile, logout } = useAuth();
  const { translate } = useLocales();
  const { memberBalance } = useSelector((x) => x.lookup);
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={handleClose}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: 250,
          backgroundColor: 'background.paper',
          boxSizing: 'border-box',
          paddingTop: '16px',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
        <Typography variant="h6" noWrap>
          {userProfile?.full_name}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ my: 2, px: 2 }}>
        <Typography variant="body2" sx={{ color: '#cbd3dc' }} noWrap>
          {userProfile?.email}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
          {`${memberBalance?.currencyCode} ${
            memberBalance?.displayBalance ? fNumberDecimal(memberBalance?.displayBalance) : 0
          }`}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ m: 2 }}>
        <MenuItem
          onClick={() => {
            navigate('/user/account');
            handleClose();
          }}
          sx={{ m: 1 }}
        >
          {translate('account')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            navigate('/user/banks');
            handleClose();
          }}
          sx={{ m: 1 }}
        >
          {translate('bank_account')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            navigate('/referral');
            handleClose();
          }}
          sx={{ m: 1 }}
        >
          {translate('referral')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            navigate('/history?tab=transaction');
            handleClose();
          }}
          sx={{ m: 1 }}
        >
          {translate('history')}
        </MenuItem>

        <MenuItem
          onClick={() =>
            dispatch(
              openConfirm({
                title: translate('logout_confirmation'),
                subTitle: translate('are_you_sure_to_logout'),
                onConfirm: handleLogout,
              })
            )
          }
          sx={{ m: 1 }}
        >
          {translate('logout')}
        </MenuItem>
      </Box>
    </Drawer>
  );
}
